import { ethers } from 'ethers';
import { useCallback, useState } from 'react';
import {
  GaslessTransferRequest,
  GaslessRelayPayload,
  NormalTransferRequest,
  PermitSignature,
} from '../type';

const RELAYER_SPENDER = '0x2910Bf2b345d295314ec2706416d69E41DA39fF9';

// EIP-712ドメイン定義
export function getEIP712Domain(contractAddress: string, chainId: number) {
  return {
    name: 'JPYC',
    version: '1',
    chainId,
    verifyingContract: contractAddress,
  };
}

// Permit型定義
export const PERMIT_TYPES = {
  Permit: [
    { name: 'owner', type: 'address' },
    { name: 'spender', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' },
  ],
};

// EIP-712ドメイン情報をコントラクトから取得
async function getEIP712DomainFromContract(
  provider: ethers.providers.Provider,
  contractAddress: string,
  chainId: number
) {
  const abi = [
    {
      name: 'name',
      inputs: [],
      outputs: [{ type: 'string' }],
      stateMutability: 'view',
      type: 'function',
    },
    {
      name: 'version',
      inputs: [],
      outputs: [{ type: 'string' }],
      stateMutability: 'view',
      type: 'function',
    },
  ] as const;
  const contract = new ethers.Contract(contractAddress, abi, provider);
  const name: string = await contract.name();
  let version = '1';
  try {
    version = await contract.version();
  } catch {
    version = '1';
  }
  return { name, version, chainId, verifyingContract: contractAddress };
}

/**
 * EIP-2612 Permit署名を生成
 * ユーザーがガス代を支払わずに、リレイヤーに送金を委託するための署名
 * Generate an EIP-2612 Permit signature
 * A signature that allows the relayer to transfer on the user's behalf without the user paying gas
 */
export async function signPermit(
  signer: ethers.Signer,
  provider: ethers.providers.Provider,
  tokenAddress: string,
  chainId: number,
  owner: string,
  spender: string,
  value: string, // JPYC amount (e.g., "100")
  deadline?: number | bigint
): Promise<PermitSignature> {
  const deadlineSeconds =
    typeof deadline === 'bigint'
      ? deadline
      : BigInt(deadline ?? Math.floor(Date.now() / 1000) + 3600);
  const valueInWei = ethers.utils.parseUnits(value, 18).toString();

  const nonceAbi = [
    {
      inputs: [{ name: 'owner', type: 'address' }],
      name: 'nonces',
      outputs: [{ name: '', type: 'uint256' }],
      stateMutability: 'view',
      type: 'function',
    },
  ] as const;
  const nonceContract = new ethers.Contract(tokenAddress, nonceAbi, provider);
  const nonce: ethers.BigNumber = await nonceContract.nonces(owner);

  const domain = await getEIP712DomainFromContract(
    provider,
    tokenAddress,
    chainId
  );
  const types = PERMIT_TYPES as any;
  const message = {
    owner,
    spender,
    value: valueInWei,
    nonce: nonce.toString(),
    deadline: deadlineSeconds.toString(),
  };

  // ethers v5 signer._signTypedData
  const signature = await (signer as any)._signTypedData(
    domain,
    types,
    message
  );
  const r = `0x${signature.slice(2, 66)}`;
  const s = `0x${signature.slice(66, 130)}`;
  const v = parseInt(signature.slice(130, 132), 16);

  return {
    owner,
    spender,
    value: valueInWei,
    deadline: deadlineSeconds.toString(),
    v,
    r,
    s,
  };
}

/**
 * リレイヤーAPIに署名を送信してガスレス送金を実行
 * 注: この関数は実際のリレイヤーサーバーが必要です
 */
export async function executeGaslessTransfer({
  chainId,
  tokenAddress,
  permitSignature,
  recipient,
  relayerEndpoint,
}: {
  permitSignature: PermitSignature;
  recipient: string;
  tokenAddress: string;
  chainId: number;
  relayerEndpoint: string;
}): Promise<{ success: boolean; txHash?: string; error?: string }> {
  try {
    // BigIntを文字列に変換してからJSON化
    const payload: GaslessRelayPayload = {
      chainId,
      tokenAddress,
      permitSignature: {
        owner: permitSignature.owner,
        spender: permitSignature.spender,
        value: permitSignature.value.toString(), // BigInt -> string
        deadline: permitSignature.deadline.toString(), // BigInt -> string
        v: permitSignature.v,
        r: permitSignature.r,
        s: permitSignature.s,
      },
      recipient,
    };

    const response = await fetch(relayerEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Gasless transfer failed:', errorData);
      throw new Error(
        errorData.error || `Relayer API error: ${response.statusText}`
      );
    }

    const result = await response.json();
    return { success: true, txHash: result.txHash };
  } catch (error) {
    console.error('Gasless transfer failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
// Return EIP-1559 fee overrides when available; fallback to legacy gasPrice
const getFeeOverrides = async (
  provider: ethers.providers.Provider
): Promise<
  | {
      maxFeePerGas?: ethers.BigNumber;
      maxPriorityFeePerGas?: ethers.BigNumber;
      gasPrice?: ethers.BigNumber;
    }
  | {}
> => {
  try {
    const fee = await provider.getFeeData();
    if (fee.maxFeePerGas && fee.maxPriorityFeePerGas) {
      return {
        maxFeePerGas: fee.maxFeePerGas,
        maxPriorityFeePerGas: fee.maxPriorityFeePerGas,
      };
    }
    if (fee.gasPrice) {
      return { gasPrice: fee.gasPrice };
    }
  } catch (e) {
    console.log('=== getFeeOverrides error', e);
  }
  return {};
};

const ERC20_ABI = [
  'function decimals() view returns (uint8)',
  'function transfer(address to, uint256 value) returns (bool)',
];

export function useTransfer() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState('');

  const resetState = useCallback(() => {
    setError('');
    setIsProcessing(false);
    setTxHash('');
  }, []);

  // Return EIP-1559 fee overrides when available; fallback to legacy gasPrice
  const getFeeOverrides = useCallback(
    async (
      provider: ethers.providers.Provider
    ): Promise<
      | {
          maxFeePerGas?: ethers.BigNumber;
          maxPriorityFeePerGas?: ethers.BigNumber;
          gasPrice?: ethers.BigNumber;
        }
      | {}
    > => {
      try {
        const fee = await provider.getFeeData();
        if (fee.maxFeePerGas && fee.maxPriorityFeePerGas) {
          return {
            maxFeePerGas: fee.maxFeePerGas,
            maxPriorityFeePerGas: fee.maxPriorityFeePerGas,
          };
        }
        if (fee.gasPrice) {
          return { gasPrice: fee.gasPrice };
        }
      } catch (e) {
        console.log('=== getFeeOverrides error', e);
      }
      return {};
    },
    []
  );

  const transfer = useCallback(
    async (
      req: NormalTransferRequest
    ): Promise<{ txHash?: string; error?: string }> => {
      setError('');
      setTxHash('');
      setIsProcessing(true);
      const {
        assetType,
        recipient,
        amount,
        tokenAddress,
        decimalsOverride,
        signer,
      } = req;

      try {
        if (!signer) {
          throw new Error('Wallet not connected');
        }
        if (!ethers.utils.isAddress(recipient)) {
          throw new Error('Invalid recipient address');
        }

        if (assetType === 'erc20') {
          if (!tokenAddress || !ethers.utils.isAddress(tokenAddress)) {
            throw new Error('Invalid token contract address');
          }
          let decimals = decimalsOverride;
          if (decimals == null) {
            const provider = signer.provider as ethers.providers.Provider;
            const c = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
            decimals = await c.decimals();
          }
          const valueUnits = ethers.utils.parseUnits(
            String(amount || '0'),
            decimals ?? 18
          );
          if (!valueUnits || valueUnits.lte(0)) {
            throw new Error('Enter a valid token amount greater than 0');
          }
          const contract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
          let gasLimitOverride: { gasLimit?: ethers.BigNumber } = {};
          try {
            const est = await contract.estimateGas.transfer(
              recipient,
              valueUnits
            );
            // pad by 20% to avoid underestimation on some RPCs
            gasLimitOverride.gasLimit = est.mul(120).div(100);
          } catch (estErr) {
            console.log('=== estimateGas transfer failed', estErr);
            gasLimitOverride.gasLimit = ethers.BigNumber.from(21000);
            // proceed without explicit gasLimit; MetaMask will prompt user
          }
          const tx = await contract.transfer(recipient, valueUnits, {
            ...gasLimitOverride,
          });
          const receipt = await tx.wait();
          setTxHash(receipt.transactionHash);
          return { txHash: receipt.transactionHash };
        }

        const valueWei = (() => {
          try {
            return ethers.utils.parseEther(String(amount || '0'));
          } catch {
            return null;
          }
        })();
        if (!valueWei || valueWei.lte(0)) {
          throw new Error('Enter a valid amount greater than 0');
        }
        let gasLimitOverride: { gasLimit?: ethers.BigNumber } = {};
        try {
          const est = await signer.estimateGas({
            to: recipient,
            value: valueWei,
          });
          // pad by 20% to avoid underestimation on some RPCs
          gasLimitOverride.gasLimit = est.mul(120).div(100);
        } catch (estErr) {
          console.log('=== estimateGas native failed', estErr);
          gasLimitOverride.gasLimit = ethers.BigNumber.from(21000);
        }
        const tx = await signer.sendTransaction({
          to: recipient,
          value: valueWei,
          ...gasLimitOverride,
        });
        const receipt = await tx.wait();
        setTxHash(receipt.transactionHash);
        return { txHash: receipt.transactionHash };
      } catch (err) {
        console.error('Transfer error:', err);
        const message = (err as Error)?.message || 'Transfer failed';
        setError(message);
        return { error: message };
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  const gaslessTransfer = useCallback(async (props: GaslessTransferRequest) => {
    try {
      setError('');
      setTxHash('');
      setIsProcessing(true);
      const {
        amount,
        recipient,
        signer,
        tokenAddress,
        relayerEndpoint,
        deadline,
      } = props;
      if (!recipient || !amount) {
        throw new Error('Please enter recipient address and amount');
      }

      if (Number(deadline) <= 0) {
        throw new Error('Invalid deadline');
      }

      if (!ethers.utils.isAddress(recipient)) {
        throw new Error('Invalid address format');
      }

      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        throw new Error('Please enter a valid amount');
      }

      if (!signer) {
        throw new Error('Wallet is not connected');
      }

      // Step 1: Generate Permit signature

      const owner = await signer.getAddress();
      const provider = signer.provider as ethers.providers.Provider;
      const network = await provider.getNetwork();
      const chainId = Number(network.chainId);
      const spender = RELAYER_SPENDER;

      const permitSignature = await signPermit(
        signer,
        provider,
        tokenAddress,
        chainId,
        owner,
        spender,
        amount,
        deadline
      );

      // Step 2: Send signature to relayer to execute gasless transfer
      const result = await executeGaslessTransfer({
        permitSignature,
        recipient,
        tokenAddress,
        chainId,
        relayerEndpoint,
      });

      if (result.error || !result.txHash) {
        throw new Error(
          result.error ||
            'Gasless transfer failed. The relayer may be unavailable.'
        );
      }

      setIsProcessing(false);
      setTxHash(result.txHash);
      return {
        txHash: result.txHash,
      };
    } catch (err) {
      setIsProcessing(false);
      console.error('Gasless transfer error:', err);
      setError('Transfer failed: ' + (err as Error).message);
      return {
        error: 'Transfer failed: ' + (err as Error).message,
      };
    }
  }, []);

  return { transfer, gaslessTransfer, resetState, isProcessing, error, txHash };
}
